import { FC, useEffect, useState } from "react";
import {
  Text,
  Heading,
  Stack,
  Grid,
  GridItem,
  Box,
  Flex,
} from "@chakra-ui/react";
import { Fragment, ReactNode } from "react";
import { formatDate } from "../../../helpers/dateUtils";
import FieldOccurencesState from "./FieldOccurencesState";
import AccordionComponent from "../../../components/atom/Accordion/AccordionComponent";
import { Indicator } from "../../../types/Indicator/Indicator";
import { getIndicatorRegionHistory } from "../../../services/Indicator/getIndicatorRegionHistory";
import { IndicatorHistory } from "../../../types/Indicator/IndicatorDataReport/IndicatorHistory";
import { PaginationValues } from "../../../types/General/PaginationValues";
import { PAGE_SIZE_OPTIONS } from "../../../utils/constants";
import TableIndicatorHistory from "../../Indicator/TableIndicatorHistory";
import useTableRendering from "../../../hooks/table/useTableRendering";
import { TableConfig } from "../../../types/Components/Table/TableConfig";
import Pagination from "../../../components/organism/Pagination/Pagination";
import { tableStyles } from "../../../utils/styles/tableStyles";
import { renderLoadingState } from "../../../hooks/table/TableStates";
import { borderRadius, borderStyle } from "../../../utils/styles/styles";
import getReferenceTextHelper from "../../../helpers/Indicator/getReferenceTextHelper";

interface OccurrenceAlertSanIndicatorFieldProps {
  indicator: Indicator;
  occurrence: any;
  index: number;
}

const OccurrenceAlertSanIndicatorField: FC<
  OccurrenceAlertSanIndicatorFieldProps
> = ({ indicator, occurrence, index }) => {
  const [historyData, setHistoryData] = useState<IndicatorHistory[]>([]);
  const [isHistoryLoaded, setIsHistoryLoaded] = useState<boolean>(false);
  const [historyTotalItems, setHistoryTotalItems] = useState<number>(0);
  const [historyPageSize, setHistoryPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [, setIsHistorySearching] = useState<boolean>(false);
  const [, setErrorFindingHistoryData] = useState(false);

  useEffect(() => {
    const fetchData = (paginationValues?: PaginationValues) => {
      setErrorFindingHistoryData(false);
      setIsHistoryLoaded(false);
      const params = {
        IndicatorId: indicator?.id,
        FrequencyId: indicator?.frequency?.id,
        ProvinceId: occurrence?.province?.id ?? "",
        CountyId: occurrence?.county?.id ?? "",
      };
      getIndicatorRegionHistory(params, undefined, paginationValues)
        .then((result) => {
          setHistoryData(result.content);
          setIsHistoryLoaded(true);
          setIsHistorySearching(false);
          setHistoryTotalItems(result.totalItems);
        })
        .catch((error) => {
          console.log("API Error:", error);
          setErrorFindingHistoryData(true);
          setIsHistorySearching(false);
        });
    };
    if (indicator?.id && occurrence?.province?.id) {
      fetchData({
        CurrentPage: 1,
        PageSize: historyPageSize,
      });
    }
  }, [
    indicator?.id,
    indicator?.frequency?.id,
    occurrence?.province?.id,
    occurrence?.county?.id,
    historyPageSize,
  ]);

  const modalTableConfig: TableConfig = {
    tableName: `Histórico do indicador (Província ${
      occurrence?.province?.name ?? ""
    })`,
    dataTable: historyData,
    setDataTable: setHistoryData,
    renderTable: (historyData) => (
      <TableIndicatorHistory
        dataTable={historyData}
        isLoaded={isHistoryLoaded}
        frequencyId={indicator?.frequency?.id}
      />
    ),
    isLoaded: isHistoryLoaded,
  };

  const {
    currentPage: historyCurrentPage,
    totalPages: historyTotalPages,
    handlePageChange: handleHistoryPageChange,
    renderContent: historyRenderContent,
  } = useTableRendering(
    modalTableConfig,
    historyPageSize,
    historyTotalItems,
    setIsHistorySearching,
  );

  const accordionTitle = indicator?.name
    ? `INDICADOR | ${indicator.name.toUpperCase()}`
    : `INDICADOR ${index + 1}`;

  const indicatorDetails: { label: string; value: ReactNode }[] = [
    {
      label: "Nome do indicador:",
      value: <Text whiteSpace="nowrap">{indicator?.name || "-"}</Text>,
    },
    {
      label: "Sub-sistema:",
      value: (
        <Text whiteSpace="nowrap">{indicator?.subSystem?.name || "-"}</Text>
      ),
    },
    {
      label: "Frequência de recolha:",
      value: (
        <Text whiteSpace="nowrap">{indicator?.frequency?.name || "-"}</Text>
      ),
    },
    {
      label: "Referências do indicador:",
      value: (
        <Text whiteSpace="nowrap">{getReferenceTextHelper(indicator)}</Text>
      ),
    },
    {
      label: "Nível:",
      value: <Text whiteSpace="nowrap">{occurrence?.level?.name || "-"}</Text>,
    },
  ];

  const details: { label: string; value: ReactNode }[] = [
    {
      label: "Criado a:",
      value: (
        <Text whiteSpace="nowrap">
          {occurrence?.createdAt
            ? formatDate(new Date(occurrence?.createdAt))
            : "-"}
        </Text>
      ),
    },
    {
      label: "Estado da ocorrência:",
      value: (
        <FieldOccurencesState
          stateValue={occurrence?.type?.id === 1 ? occurrence?.isActive : null}
          additionalStyle={{ display: "inline-block" }}
        />
      ),
    },
    {
      label: "Nível:",
      value: <Text whiteSpace="nowrap">{occurrence?.level?.name || "-"}</Text>,
    },
    {
      label: "Origem da ocorrência:",
      value: <Text whiteSpace="nowrap">{occurrence?.origin || "-"}</Text>,
    },
    {
      label: "Província:",
      value: (
        <Text whiteSpace="nowrap">{occurrence?.province?.name || "-"}</Text>
      ),
    },
    {
      label: "Município:",
      value: <Text whiteSpace="nowrap">{occurrence?.county?.name || "-"}</Text>,
    },
    {
      label: "Detalhes sobre a ocorrência:",
      value: <Text>{occurrence?.description || "-"}</Text>,
    },
  ];

  return (
    <AccordionComponent accordionTitle={accordionTitle} fontSize="sm">
      <Stack gap="8">
        <Stack gap="4">
          <Heading as="h4" fontSize="xl">
            Sobre a ocorrência
          </Heading>
          <Grid
            templateColumns={{ base: "1fr", md: "max-content 1fr" }}
            columnGap={{ base: 0, md: 6 }}
            rowGap={2}
          >
            {details.map(({ label, value }) => (
              <Fragment key={label}>
                <GridItem>
                  <Text fontWeight="bold" whiteSpace="nowrap">
                    {label}
                  </Text>
                </GridItem>
                <GridItem>{value}</GridItem>
              </Fragment>
            ))}
          </Grid>
        </Stack>

        <Stack gap="4">
          <Heading as="h4" fontSize="xl">
            Dados do indicador
          </Heading>
          <Grid
            templateColumns={{ base: "1fr", md: "max-content 1fr" }}
            columnGap={{ base: 0, md: 6 }}
            rowGap={2}
          >
            {indicatorDetails.map(({ label, value }) => (
              <Fragment key={label}>
                <GridItem>
                  <Text fontWeight="bold" whiteSpace="nowrap">
                    {label}
                  </Text>
                </GridItem>
                <GridItem>{value}</GridItem>
              </Fragment>
            ))}
          </Grid>
        </Stack>

        {isHistoryLoaded ? (
          <Stack gap="4">
            <Flex
              direction="row"
              gap="4"
              justifyContent="space-between"
              alignItems="center"
            >
              <Heading as="h4" fontSize="xl">
                {modalTableConfig.tableName}
              </Heading>
              <Pagination
                totalResults={
                  historyTotalItems ? historyTotalItems : historyData.length
                }
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalPages={historyTotalPages}
                currentPage={historyCurrentPage}
                pageSize={historyPageSize}
                handlePageChange={handleHistoryPageChange}
                setPageSize={setHistoryPageSize}
              />
            </Flex>
            <Box
              width="100%"
              display="flex"
              flexDirection="column"
              sx={tableStyles}
            >
              {historyRenderContent()}
            </Box>
          </Stack>
        ) : (
          <Box
            border={borderStyle}
            borderBottomRadius={borderRadius}
            padding="3"
          >
            {renderLoadingState()}
          </Box>
        )}
      </Stack>
    </AccordionComponent>
  );
};

export default OccurrenceAlertSanIndicatorField;
